import {DataFrame, DataFrameColumnDef, DataFrameRow} from 'data-frames/DataFrameTypes';
import {nullableNumericAccessor} from 'data-frames/util/AccessorUtils';
import {DataFrameValueAccessor} from 'data-frames/util/createAccessors';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {CustomColumnDataType} from 'notebook/structured-tables/custom-columns/CustomColumnConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {TableColumnType, Cell, BaseTypedValueType} from 'notebook/tables/TableConstants';
import {JSONValue} from 'util/Types';

import {
  DataFrameToTableColumnInfoInitializer,
  BaseColumnConstraint,
  BaseStructuredTableColumn,
} from '../DataFrameTableEditableTypes';

export default function getNumericalColumnInfoInitializer(
  leafType: LeafType.DECIMAL | LeafType.INTEGER
): DataFrameToTableColumnInfoInitializer {
  return (
    {displayName, type: {isNullable, leafType}}: DataFrameColumnDef,
    dataFrame: DataFrame,
    baseColumnConstraint: BaseColumnConstraint,
    baseStructuredTableColumn: BaseStructuredTableColumn,
    baseAccessor: DataFrameValueAccessor
  ): ReturnType<DataFrameToTableColumnInfoInitializer> => {
    const rawValueAccessor = nullableNumericAccessor(baseAccessor, leafType);

    return {
      columnConstraint: {
        ...baseColumnConstraint,
        columnDefinition: {
          type: TableColumnType.CUSTOM,
          name: displayName,
          customColumnConfig: {
            dataType:
              leafType === LeafType.DECIMAL ? CustomColumnDataType.FLOAT : CustomColumnDataType.INTEGER,
          },
          isRequired: !isNullable,
        },
        structuredTableColumn: {
          ...makeStructuredTableColumn({
            ...baseStructuredTableColumn,
            columnType: TableColumnType.CUSTOM,
          }),
          ...baseStructuredTableColumn,
        },
      },
      dataFrameValueToTableCell: (row: DataFrameRow): Cell => {
        const rawValue = rawValueAccessor(row);
        return {
          text: '',
          typedValue:
            rawValue == null
              ? null
              : {
                  type: BaseTypedValueType.NUMBER,
                  rawValue,
                },
        };
      },
      tableCellToDataFrameValue: ({text, typedValue}: Cell): JSONValue => {
        if (typedValue != null && typedValue.type === BaseTypedValueType.NUMBER) {
          return typedValue.rawValue;
        }

        const num = leafType === LeafType.DECIMAL ? parseFloat(text) : parseInt(text, 10);
        return Number.isNaN(num) ? null : num;
      },
    };
  };
}
